/** Shared types for contract historical analysis (modal + fullscreen analytics). */

export type HistoricalSource =
  | 'bid' | 'ask' | 'volume' | 'trades' | 'oi' | 'delta' | 'gamma' | 'spot_gamma' | 'premium' | 'theta' | 'vega' | 'haltz'
  | 'last' | 'mid' | 'vol_oi'
  | 'rho' | 'lambda' | 'theoretical'
  | 'zahn' | 'mal';

export interface ContractHistoricalParams {
  underlying: string;
  strike: number | string;
  expiry?: string;
  optionType?: 'CALL' | 'PUT' | 'call' | 'put';
  source?: HistoricalSource;
  /** Today snapshot values so EOD chart shows whether accumulated volume is opening or closing. */
  todayVolume?: number;
  todayOI?: number;
}

export interface EodRecord {
  contract?: string;
  underlying_symbol?: string;
  exp_date?: string;
  type?: string;
  strike?: number;
  tradetime?: string;
  volume?: number;
  open_interest?: number;
  bid?: number;
  ask?: number;
  bid_size?: number;
  ask_size?: number;
  last?: number;
  midpoint?: number;
  open?: number;
  high?: number;
  low?: number;
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  volatility?: number;
  spot_gamma?: number | null;
}

/** Today's live snapshot injected by backend into EOD response.
 * Allows comparing today's OI/Volume against prior EOD to see if positions are opening or closing. */
export interface EodTodaySnapshot {
  tradetime: string;
  open_interest: number;
  volume: number;
  last: number;
  bid: number;
  ask: number;
  implied_volatility: number;
  delta: number;
  gamma: number;
  underlying_price: number;
}

export interface TapeSummary {
  strategy_id?: string;
  strategy_note?: string;
  bid_side_contracts?: number;
  ask_side_contracts?: number;
  between_contracts?: number;
  unknown_contracts?: number;
  trades_count?: number;
  quote_ladder_points?: number;
  classified_pct?: number;
}

export interface IntradayRow {
  time: string;
  datetime?: string;
  price?: number;
  size?: number;
  premium?: number;
  underlying_price?: number;
  condition_codes?: number[];
  condition_label?: string;
  condition_color?: string;
  condition_description?: string;
  exchange?: number;
  tape_side?: 'at_bid' | 'at_ask' | 'between' | 'unknown' | string;
  execution_side?: 'buy' | 'sell' | 'mid' | string;
  nbbo_bid?: number | null;
  nbbo_ask?: number | null;
  quote_ms_of_day?: number | null;
}

export interface TapeByDayRow {
  date: string;
  has_trades: boolean;
  trades_count: number;
  tape_summary: TapeSummary;
}

export interface IntradayAvailableDate {
  date: string;
  tradesCount: number;
}

export type HistoricalChartLayout = 'modal' | 'fullscreen';

export const DEFAULT_INTRADAY_CHART_POINTS = 2500;
export const EOD_PAGE_LIMIT = 1000;
export const EOD_MAX_PAGES = 10;
export const EOD_PAGE_FETCH_CONCURRENCY = 5;
export const MAX_RANGE_DAYS = 365;
export const RELATIVE_UNUSUAL_THRESHOLD = 3;
